/**
 * HubDashboard — the Hub space's dashboard: internal content library (policies/processes/
 * customer-assurance/training), not security/ISMS operational content. Same page shape as
 * SecurityDashboard.tsx (hero + quick actions, stat cards, charts, recent-items tables), with
 * data pulled from the four document registers HUB_CATEGORIES maps to.
 */
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import ReactApexChart from 'react-apexcharts';
import { documentApi } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { HUB_CATEGORIES, STATUS_META, effectiveStatus, formatUpdated } from './hub/hubUtils';

interface Register {
  registerId: string;
  name?: string;
}

interface HubDoc {
  documentId: string;
  title?: string;
  status?: string;
  nextReviewDate?: string;
  updatedAt?: number;
}

type TaggedDoc = HubDoc & { catKey: string; catLabel: string };

const donutOptions = (
  labels: string[],
  colors: string[],
  center: { value: number; label: string; color: string },
): ApexCharts.ApexOptions => ({
  chart: { type: 'donut' },
  labels,
  colors,
  stroke: { width: 0 },
  dataLabels: { enabled: false },
  legend: { position: 'bottom', fontSize: '13px' },
  plotOptions: {
    pie: {
      donut: {
        size: '68%',
        labels: {
          show: true,
          total: {
            show: true,
            showAlways: true,
            label: center.label,
            color: center.color,
            fontSize: '13px',
            fontWeight: 500,
            formatter: () => String(center.value),
          },
        },
      },
    },
  },
  tooltip: { y: { formatter: (v: number) => `${v} documents` } },
});

/** Radial percentage gauge — copied verbatim from SecurityDashboard.tsx's FrameworkGauge (same
 * stroke-dasharray technique as CatalogPage.tsx's MetricRing), sized for a 4-up grid of
 * at-a-glance percentages. */
const FrameworkGauge: React.FC<{ pct: number; color: string }> = ({ pct, color }) => {
  const size = 72;
  const stroke = 7;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const dash = (Math.max(0, Math.min(100, pct)) / 100) * circumference;
  return (
    <div className="np-gauge">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="var(--np-border)" strokeWidth={stroke} />
        <circle
          cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color} strokeWidth={stroke}
          strokeDasharray={`${dash} ${circumference}`} strokeLinecap="round"
        />
      </svg>
      <span className="np-gauge-value" style={{ color }}>{pct}%</span>
    </div>
  );
};

// Categorical colors for the "Documents by Category" donut — real design-system tone tokens,
// one per HUB_CATEGORIES entry, in HUB_CATEGORIES order (not a status/tone mapping, just four
// distinct swatches from the same palette used everywhere else in the app).
const CATEGORY_COLORS = ['var(--bs-primary)', 'var(--bs-info)', 'var(--bs-warning)', 'var(--bs-success)'];

export const HubDashboard: React.FC = () => {
  const { user } = useAuth();
  const displayName = (user as any)?.name || (user as any)?.email?.split('@')[0] || 'there';

  const { data, isLoading } = useQuery({
    queryKey: ['hubDashboard'],
    queryFn: async () => {
      const registers = ((await documentApi.getRegisters()) || []) as Register[];
      const docsByCategory = await Promise.all(
        HUB_CATEGORIES.map((cat) => {
          const register = registers.find((r) => r.name === cat.registerName);
          return register ? documentApi.listByRegister(register.registerId) : Promise.resolve([]);
        }),
      );
      return { docsByCategory: docsByCategory as HubDoc[][] };
    },
  });

  const categories = useMemo(
    () => HUB_CATEGORIES.map((cat, i) => ({ cat, docs: data?.docsByCategory[i] || [] })),
    [data],
  );

  const allDocs: TaggedDoc[] = useMemo(
    () => categories.flatMap(({ cat, docs }) => docs.map((doc) => ({ ...doc, catKey: cat.key, catLabel: cat.shortLabel }))),
    [categories],
  );

  const published = useMemo(() => allDocs.filter((d) => effectiveStatus(d) === 'published').length, [allDocs]);
  const stale = useMemo(() => allDocs.filter((d) => effectiveStatus(d) === 'stale').length, [allDocs]);
  const draft = useMemo(() => allDocs.filter((d) => effectiveStatus(d) === 'draft').length, [allDocs]);
  const total = allDocs.length;

  const recentlyUpdated = useMemo(
    () => [...allDocs].sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0)).slice(0, 5),
    [allDocs],
  );

  // Most overdue first — same "worst first" instinct as SecurityDashboard's Framework Readiness
  // gauges, so the review that has been due longest surfaces at the top of the list.
  const reviewDue = useMemo(
    () => allDocs
      .filter((d) => effectiveStatus(d) === 'stale')
      .sort((a, b) => new Date(a.nextReviewDate || 0).getTime() - new Date(b.nextReviewDate || 0).getTime())
      .slice(0, 5),
    [allDocs],
  );

  const categoryGauges = useMemo(
    () => categories.map(({ cat, docs }) => {
      const catTotal = docs.length;
      const catPublished = docs.filter((d) => effectiveStatus(d) === 'published').length;
      return { cat, total: catTotal, published: catPublished, pct: catTotal > 0 ? Math.round((catPublished / catTotal) * 100) : 0 };
    }),
    [categories],
  );

  const donutLabels = HUB_CATEGORIES.map((c) => c.shortLabel);
  const donutSeries = categories.map(({ docs }) => docs.length);
  const donutCenter = { value: total, label: 'Documents', color: 'var(--bs-heading-color)' };

  if (isLoading) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
        <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading…</span></div>
      </div>
    );
  }

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">

      {/* Welcome / Hero Row */}
      <div className="np-grid np-grid-8-4">
        <div className="np-card">
          <div className="np-card-body d-flex align-items-start justify-content-between">
            <div className="flex-grow-1">
              <h2 style={{ marginBottom: 4, fontSize: '1.375rem', fontWeight: 700, color: 'var(--bs-heading-color)', lineHeight: 1.3 }}>Welcome back, {displayName}! 👋</h2>
              <p className="np-sub" style={{ marginBottom: '1rem' }}>Here's what's happening across your content library today.</p>
              <div className="row g-3">
                {[
                  { count: published, label: STATUS_META.published.label, tone: 'success', icon: STATUS_META.published.icon },
                  { count: stale, label: 'Review Due', tone: 'warning', icon: STATUS_META.stale.icon },
                  { count: draft, label: STATUS_META.draft.label, tone: 'neutral', icon: STATUS_META.draft.icon },
                  { count: total, label: 'Total Documents', tone: 'info', icon: 'ri-file-list-3-line' },
                ].map((item) => (
                  <div key={item.label} className="col-6 col-md-3">
                    <div className="d-flex align-items-center gap-2">
                      <span className={`np-icon-badge np-tone-${item.tone}`}><i className={item.icon}></i></span>
                      <div>
                        <div className="np-tabular" style={{ fontWeight: 600, lineHeight: 1 }}>{item.count}</div>
                        <small className="np-hint">{item.label}</small>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 d-flex gap-2 flex-wrap">
                <Link to="/hub/policies" className="np-btn np-btn-primary np-btn-sm"><i className="ri-book-open-line"></i>Browse Content</Link>
                <Link to="/hub/service-catalog" className="np-btn np-btn-ghost np-btn-sm"><i className="ri-archive-drawer-line"></i>Service Catalog</Link>
              </div>
            </div>
            <i className="ri-book-2-line ms-3 d-none d-md-block" style={{ fontSize: '3rem', color: 'var(--bs-primary)', opacity: 0.25 }}></i>
          </div>
        </div>
        <div className="np-card">
          <div className="np-card-head"><h2>Quick Actions</h2></div>
          <div className="np-card-body d-flex flex-column gap-2">
            {HUB_CATEGORIES.map((cat) => (
              <Link key={cat.key} to={`/hub/${cat.key}`} className="np-btn np-btn-ghost np-btn-sm w-100 justify-content-start">
                <i className={cat.icon}></i>Browse {cat.shortLabel}
              </Link>
            ))}
            <Link to="/hub/service-catalog" className="np-btn np-btn-ghost np-btn-sm w-100 justify-content-start"><i className="ri-archive-drawer-line"></i>Service Catalog</Link>
          </div>
        </div>
      </div>

      {/* Stat cards — one per HUB_CATEGORIES entry, in HUB_CATEGORIES order */}
      <div className="np-grid np-grid-4">
        {categories.map(({ cat, docs }) => {
          const catStale = docs.filter((d) => effectiveStatus(d) === 'stale').length;
          const catPublished = docs.filter((d) => effectiveStatus(d) === 'published').length;
          return (
            <div key={cat.key} className="np-card">
              <div className="np-card-body np-stat">
                <Link to={`/hub/${cat.key}`} className="np-stat-icon np-tone-primary text-decoration-none">
                  <i className={cat.icon}></i>
                </Link>
                <p className="np-stat-label">
                  <Link to={`/hub/${cat.key}`} className="text-decoration-none np-sub">{cat.shortLabel}</Link>
                </p>
                <p className="np-stat-value np-tabular">{docs.length}</p>
                <p className="np-stat-trend" style={{ color: catStale > 0 ? 'var(--bs-warning)' : 'var(--bs-success)' }}>
                  {catStale > 0
                    ? <><i className={STATUS_META.stale.icon}></i>{catStale} review due</>
                    : <><i className={STATUS_META.published.icon}></i>{catPublished} published</>}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Charts row */}
      <div className="np-grid np-grid-2">
        <div className="np-card">
          <div className="np-card-head"><h2>Documents by Category</h2></div>
          <div className="np-card-body">
            {total === 0 ? (
              <div className="text-center py-4 np-sub">
                <i className="ri-pie-chart-line d-block mb-2" style={{ fontSize: '2rem', opacity: 0.5 }}></i>No documents yet
              </div>
            ) : (
              <ReactApexChart type="donut" height={300} series={donutSeries}
                options={donutOptions(donutLabels, CATEGORY_COLORS, donutCenter)} />
            )}
          </div>
        </div>
        <div className="np-card">
          <div className="np-card-head"><h2>Content Health</h2></div>
          <div className="np-card-body">
            {total === 0 ? (
              <div className="text-center py-4 np-sub">
                <i className="ri-shield-check-line d-block mb-2" style={{ fontSize: '2rem', opacity: 0.5 }}></i>
                No documents yet — nothing to measure
              </div>
            ) : (
              <div className="np-gauge-cluster">
                {categoryGauges.map(({ cat, total: catTotal, published: catPublished, pct }) => {
                  const color = pct >= 80 ? 'var(--bs-success)' : pct >= 50 ? 'var(--bs-warning)' : 'var(--bs-info)';
                  return (
                    <div key={cat.key} className="np-gauge-card">
                      <FrameworkGauge pct={pct} color={color} />
                      <span className="np-gauge-name">{cat.shortLabel}</span>
                      <span className="np-gauge-sub">{catPublished} of {catTotal}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recently Updated + Review Due */}
      <div className="np-grid np-grid-2">
        <div className="np-card">
          <div className="np-card-head">
            <h2>Recently Updated</h2>
            <Link to="/hub/policies" className="np-btn np-btn-ghost np-btn-sm">View All</Link>
          </div>
          <div className="np-card-body" style={{ padding: 0 }}>
            <div className="np-table-wrap">
              <table className="np-table">
                <thead><tr><th>Title</th><th>Category</th><th>Status</th></tr></thead>
                <tbody>
                  {recentlyUpdated.length === 0 ? (
                    <tr><td colSpan={3} className="text-center np-sub py-4"><i className="ri-file-text-line me-1"></i>No documents found</td></tr>
                  ) : recentlyUpdated.map((doc) => {
                    const meta = STATUS_META[effectiveStatus(doc)];
                    return (
                      <tr key={doc.documentId}>
                        <td><Link to={`/hub/documents/${encodeURIComponent(doc.documentId)}`} className="np-row-title">{doc.title || 'Untitled'}</Link></td>
                        <td><span className="np-chip np-tone-neutral">{doc.catLabel}</span></td>
                        <td><span className={`np-chip ${meta.tone}`}><i className={meta.icon}></i>{meta.label}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="np-card">
          <div className="np-card-head">
            <h2>Review Due</h2>
          </div>
          <div className="np-card-body" style={{ padding: 0 }}>
            <div className="np-table-wrap">
              <table className="np-table">
                <thead><tr><th>Title</th><th>Category</th><th>Next Review Date</th></tr></thead>
                <tbody>
                  {reviewDue.length === 0 ? (
                    <tr><td colSpan={3} className="text-center np-sub py-4"><i className="ri-checkbox-circle-line me-1"></i>Nothing due for review</td></tr>
                  ) : reviewDue.map((doc) => (
                    <tr key={doc.documentId}>
                      <td><Link to={`/hub/documents/${encodeURIComponent(doc.documentId)}`} className="np-row-title">{doc.title || 'Untitled'}</Link></td>
                      <td><span className="np-chip np-tone-neutral">{doc.catLabel}</span></td>
                      <td className="np-tabular">{formatUpdated(doc.nextReviewDate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};
